<template>
    <div v-if="isActive && isReady" class="tour-container">
        <div class="overlay"></div>
        <div v-if="highlightStyles" class="highlight-border" :style="highlightStyles"></div>

        <div class="step" :style="positionStyles" :class="arrowClass">
            <div class="close-icon" @click="finishTour">
                <i class="material-icons">close</i>
            </div>
            <div class="content">
                <img v-if="currentStep.gif" :src="currentStep.gif" class="step-gif" />
                <h3>{{ currentStep.title }}</h3>
                <div v-html="currentStep.content"></div>

                <div class="actions">
                    <button v-if="currentIndex > 0" class="btn back" @click="prevStep">{{ $t("message.back") }}</button>
                    <button class="btn next" @click="nextStep">
                        {{ isLastStep ? $t("tour.finish") : $t("message.forward") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { publishMessage, SystemMessageTypes } from "../../utils/systemPubSub";

export default {
    name: "SetupTour",
    props: {
        steps: { type: Array, required: true },
        isActive: { type: Boolean, default: false },
    },
    data() {
        return {
            currentIndex: 0,
            isReady: false,
            position: { top: "0px", left: "0px" },
            highlightStyles: null,
            currentUrl: null,
        };
    },
    computed: {
        currentStep() {
            return this.steps[this.currentIndex];
        },
        isLastStep() {
            return this.currentIndex === this.steps.length - 1;
        },
        arrowClass() {
            return `arrow-${this.currentStep.arrowPosition}`;
        },
        positionStyles() {
            return this.position;
        },
    },
    watch: {
        isActive: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.startTour();
                } else {
                    this.isReady = false;
                }
            },
        },
    },
    methods: {
        handleKeydown(event) {
            if (!this.isActive) return;
            if (event.key === "ArrowRight") {
                this.nextStep();
            } else if (event.key === "ArrowLeft") {
                this.prevStep();
            }
        },
        async startTour() {
            if (!this.currentStep) return;
            this.currentUrl = this.$route.path;
            this.currentIndex = 0;
            await this.handleNavigation(this.currentStep.routerUrl);
        },

        async nextStep() {
            if (this.isLastStep) {
                return this.finishTour();
            }
            const nextIndex = this.currentIndex + 1;
            await this.goToStep(nextIndex);
        },

        async prevStep() {
            if (this.currentIndex === 0) return;
            const prevIndex = this.currentIndex - 1;
            await this.goToStep(prevIndex);
        },

        async goToStep(index) {
            const step = this.steps[index];
            if (!step) return;

            if (step.routerUrl && this.$route.path !== step.routerUrl) {
                await this.handleNavigation(step.routerUrl);
            }

            this.currentIndex = index;
            await this.$nextTick();
            this.schedulePositionUpdate();

            if (step.action && step.action !== "modal-opened") {
                this.performAction(step.action);
            }
        },

        finishTour() {
            this.$emit("tour-finished");
            if (this.currentUrl) {
                this.$router.push(this.currentUrl);
            }
        },

        async handleNavigation(url) {
            if (!url || this.$route.path === url) {
                await this.waitForElement(this.currentStep.target);
                this.schedulePositionUpdate();
                return;
            }
            try {
                await this.$router.push(url);
                await this.waitForElement(this.currentStep.target);
                this.schedulePositionUpdate();
            } catch (error) {
                console.error("Navigation error:", error);
                this.schedulePositionUpdate();
            }
        },

        async waitForElement(selector) {
            return new Promise((resolve) => {
                const el = document.querySelector(selector);
                if (el) {
                    resolve(true);
                    return;
                }

                let attempts = 0;
                const maxAttempts = 10;
                const interval = setInterval(() => {
                    attempts++;
                    const element = document.querySelector(selector);
                    if (element) {
                        clearInterval(interval);
                        resolve(true);
                    } else if (attempts >= maxAttempts) {
                        clearInterval(interval);
                        console.warn(`Element not found: ${selector}`);
                        resolve(false);
                    }
                }, 50);

                setTimeout(() => {
                    clearInterval(interval);
                    console.warn(`Element not found: ${selector}`);
                    resolve(false);
                }, 50);
            });
        },

        schedulePositionUpdate() {
            clearTimeout(this._positionTimeout);
            this._positionTimeout = setTimeout(() => {
                this.isReady = true;
                this.updatePosition();
            }, 20);
        },

        updatePosition() {
            if (!this.currentStep) return;
            const el = document.querySelector(this.currentStep.target);
            if (!el) return;

            const rect = el.getBoundingClientRect();
            const stepWidth = 320;
            const stepHeight = 200;
            const spacing = 10;

            const { scrollY, scrollX, innerWidth, innerHeight } = window;

            let top = 0;
            let left = 0;

            switch (this.currentStep.arrowPosition) {
                case "top":
                    top = rect.bottom + scrollY + spacing;
                    left = rect.left + scrollX;
                    break;
                case "bottom":
                    top = rect.top + scrollY - stepHeight - spacing;
                    left = rect.left + scrollX;
                    break;
                case "left":
                    top = rect.top + scrollY;
                    left = rect.right + scrollX + spacing;
                    break;
                case "right":
                    top = rect.top + scrollY;
                    left = rect.left + scrollX - stepWidth - spacing;
                    break;
            }

            // Clamp position
            top = Math.max(scrollY + 20, Math.min(top, scrollY + innerHeight - stepHeight - 20)) - (this.currentStep.marginBottom || 0);
            left = Math.max(20, Math.min(left, innerWidth - stepWidth - 20));

            this.position = { top: `${top}px`, left: `${left}px` };
            this.highlightStyles = {
                top: `${rect.top + scrollY - 5}px`,
                left: `${rect.left + scrollX - 5}px`,
                width: `${rect.width + 10}px`,
                height: `${rect.height + 10}px`,
            };
        },

        performAction(action) {
            console.log("Perform Action: ", action);
            publishMessage(SystemMessageTypes.TOUR_ACTION_PERFORMED, { action });
        },
    },

    mounted() {
        window.addEventListener("resize", this.updatePosition);
        window.addEventListener("scroll", this.updatePosition);
        window.addEventListener("keydown", this.handleKeydown);
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.updatePosition);
        window.removeEventListener("scroll", this.updatePosition);
        window.removeEventListener("keydown", this.handleKeydown);
    },
};
</script>

<style lang="scss" scoped>
.tour-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 9999;
    pointer-events: none;

    .overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.1);
        z-index: 1;
        pointer-events: auto;
    }

    .highlight-border {
        position: absolute;
        border: 3px solid #ffa500;
        border-radius: 8px;
        z-index: 2;
        transition: all 0.3s ease;
        box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6), 0 0 12px 4px rgba(255, 165, 0, 0.5);
        background: transparent;
        pointer-events: none;
    }

    .step {
        position: absolute;
        z-index: 3;
        background: #fff;
        padding: 20px;
        border-radius: 12px;
        width: 320px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        pointer-events: auto;
        font-family: "Arial", sans-serif;

        .close-icon {
            position: absolute;
            top: 20px;
            right: 20px;
            cursor: pointer;
            font-size: 20px;
            z-index: 4;
            user-select: none;
            background-color: #fff;
            border-bottom-left-radius: 12px;

            i {
                font-size: 24px;
                color: #666;
            }
        }

        &.arrow-top::before,
        &.arrow-bottom::before,
        &.arrow-left::before,
        &.arrow-right::before {
            content: "";
            position: absolute;
            border: 8px solid transparent;
        }

        &.arrow-top::before {
            top: -16px;
            left: 20px;
            border-bottom-color: white;
        }

        &.arrow-bottom::before {
            bottom: -16px;
            left: 20px;
            border-top-color: white;
        }

        &.arrow-left::before {
            left: -16px;
            top: 20px;
            border-right-color: white;
        }

        &.arrow-right::before {
            right: -16px;
            top: 20px;
            border-left-color: white;
        }

        .content {
            h3 {
                margin: 0 0 10px;
                font-size: 18px;
                color: #333;
                white-space: normal;
                word-wrap: break-word;
                overflow-wrap: break-word;
            }

            p {
                margin: 0 0 10px;
                color: #555;
            }

            .step-gif {
                user-select: none;
                max-width: 100%;
                border-radius: 8px;
                margin-bottom: 12px;
            }

            .actions {
                margin-top: 12px;
                display: flex;
                justify-content: space-between;
                gap: 8px;

                .btn {
                    flex: 1;
                    padding: 8px 14px;
                    border: none;
                    border-radius: 6px;
                    font-size: 14px;
                    cursor: pointer;
                    transition: background 0.3s;

                    &.back {
                        background-color: #eee;
                        color: #333;
                    }

                    &.next {
                        background-color: #ff9800;
                        color: white;
                    }
                }
            }
        }
    }
}
</style>
